import { useCallback, useMemo, useRef, useState } from 'react'
import SummaryDashboard from '../components/snoop/SummaryDashboard'
import MappingAddForm from '../components/snoop/MappingAddForm'
import {
  fetchLuminateStatusMapping,
  updateLuminateStatus,
  deleteLuminateStatusMapping,
  uploadLuminateStatusBatch,
} from '../api/luminateClient'
import { LUMINATE_BARE_STATUS_OPTIONS, luminateStoredStatus } from '../utils/snoop/statusOptions'
import { PLACEHOLDER_LUMINATE_MAPPING } from '../utils/snoop/placeholderColumns'

const ENTITY_FILTER_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'label', label: 'Label' },
  { value: 'artist', label: 'Artist' },
  { value: 'track', label: 'Track' },
]

const ENTITY_TYPE_OPTIONS = [
  { value: 'label', label: 'Label' },
  { value: 'artist', label: 'Artist' },
  { value: 'track', label: 'Track' },
]

const btnClass =
  'inline-flex shrink-0 items-center rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:opacity-60'

function payloadForRow({ status, row }) {
  const tier = String(row?.['Entity Type'] || '').toLowerCase()
  const key = String(row?.['Entity Key'] || '').trim()
  const bare = luminateStoredStatus(status) || String(status || '').trim()
  const base = {
    status: bare,
    entityType: tier || null,
    trackTitle: row?.Title || null,
    primaryArtist: row?.['Primary Artist'] || null,
    displayArtist: row?.['Primary Artist'] || null,
  }
  if (tier === 'label') return { ...base, imprint: key }
  if (tier === 'artist') return { ...base, primaryArtist: key, displayArtist: key }
  return {
    ...base,
    songLuminateId: key,
    primaryArtist: row?.['Primary Artist'] || null,
    trackTitle: row?.Title || null,
  }
}

export default function LuminateMappingPage() {
  const [reloadNonce, setReloadNonce] = useState(0)
  const [entityType, setEntityType] = useState('')
  const [batchUploading, setBatchUploading] = useState(false)
  const [batchResult, setBatchResult] = useState(null)
  const [batchError, setBatchError] = useState(null)
  const fileInputRef = useRef(null)

  const fetchData = useCallback(
    () =>
      fetchLuminateStatusMapping({
        entityType: entityType || undefined,
      }),
    // reloadNonce is intentionally unused inside the callback: bumping it must
    // produce a new fetchData identity so SummaryDashboard refetches after an edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityType, reloadNonce],
  )
  const bump = useCallback(() => setReloadNonce((n) => n + 1), [])

  const editableStatus = useMemo(
    () => ({
      Status: {
        target: 'luminate',
        uidColumn: 'Entity Key',
        options: LUMINATE_BARE_STATUS_OPTIONS,
        onSave: async ({ status, row }) => {
          const tier = String(row?.['Entity Type'] || '').toLowerCase()
          if (!tier || !['label', 'artist', 'track'].includes(tier)) {
            throw new Error('Row is missing Entity Type (label / artist / track).')
          }
          await updateLuminateStatus(payloadForRow({ status, row }))
        },
      },
    }),
    [],
  )

  const onDeleteRow = useCallback(
    async (row) => {
      await deleteLuminateStatusMapping({
        entityType: row?.['Entity Type'],
        entityKey: row?.['Entity Key'],
      })
      bump()
    },
    [bump],
  )

  const onBatchFile = useCallback(
    async (event) => {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file) return
      setBatchUploading(true)
      setBatchError(null)
      setBatchResult(null)
      try {
        const result = await uploadLuminateStatusBatch(file)
        setBatchResult(result)
        if (result?.succeeded > 0) bump()
      } catch (err) {
        setBatchError(err?.message || 'Batch upload failed')
      } finally {
        setBatchUploading(false)
      }
    },
    [bump],
  )

  const addForm = useMemo(
    () => (
      <MappingAddForm
        title="Add / update Luminate status"
        submitLabel="Save status"
        fields={[
          {
            name: 'entityType',
            label: 'Entity Type',
            type: 'select',
            required: true,
            options: ENTITY_TYPE_OPTIONS,
            defaultValue: 'label',
            clearOnSuccess: false,
          },
          {
            name: 'status',
            label: 'Status',
            type: 'select',
            required: true,
            options: LUMINATE_BARE_STATUS_OPTIONS,
            defaultValue: 'Signed',
            clearOnSuccess: false,
          },
          {
            name: 'key',
            label: 'Key (Imprint / Artist / Song ID)',
            placeholder: 'Depends on entity type…',
            required: true,
          },
          {
            name: 'title',
            label: 'Title (track optional)',
            placeholder: 'Track title…',
            clearOnSuccess: true,
          },
          {
            name: 'primaryArtist',
            label: 'Primary Artist (optional)',
            placeholder: 'Artist name…',
            clearOnSuccess: true,
          },
        ]}
        onSubmit={async (values) => {
          const status = luminateStoredStatus(values.status) || String(values.status || '').trim()
          const key = String(values.key || '').trim()
          const tier = String(values.entityType || '').toLowerCase()
          if (!['label', 'artist', 'track'].includes(tier)) {
            throw new Error('Entity Type is required (label / artist / track).')
          }
          const body = {
            status,
            entityType: tier,
            trackTitle: values.title || null,
            primaryArtist: values.primaryArtist || null,
            displayArtist: values.primaryArtist || null,
          }
          if (tier === 'label') body.imprint = key
          else if (tier === 'artist') {
            body.primaryArtist = key
            body.displayArtist = key
          } else body.songLuminateId = key
          await updateLuminateStatus(body)
          bump()
        }}
      />
    ),
    [bump],
  )

  const extraActions = (
    <div className="flex flex-wrap items-center gap-2">
      <label className="inline-flex items-center gap-2 text-sm text-gray-600">
        Type
        <select
          className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm"
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
        >
          {ENTITY_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        className="hidden"
        onChange={onBatchFile}
      />
      <button
        type="button"
        className={btnClass}
        disabled={batchUploading}
        title="CSV/Excel: Status + Imprint, Primary Artist, or Luminate Track ID. Side-by-side tables OK (blank column between). Track ID rows need entity-suffixed Status."
        onClick={() => fileInputRef.current?.click()}
      >
        {batchUploading ? 'Uploading…' : 'Upload statuses'}
      </button>
    </div>
  )

  const batchBanner = batchError ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
      Batch upload failed: {batchError}
    </div>
  ) : batchResult ? (
    <div
      className={[
        'rounded-xl border px-4 py-3 text-sm',
        batchResult.failed
          ? 'border-amber-200 bg-amber-50 text-amber-950'
          : 'border-emerald-200 bg-emerald-50 text-emerald-950',
      ].join(' ')}
    >
      <p className="font-medium">
        Batch upload: {batchResult.succeeded ?? 0} succeeded
        {batchResult.failed ? `, ${batchResult.failed} failed` : ''}
        {' · '}
        {batchResult.processed ?? 0} processed
      </p>
      <p className="mt-1 text-xs opacity-90">
        Columns: Status + one of Imprint / Primary Artist / Luminate Track ID. Side-by-side
        tables are OK (blank column between). Track ID rows require entity-suffixed Status
        (e.g. Signed Track); label status resolves Imprint from the track.
      </p>
      {Array.isArray(batchResult.errors) && batchResult.errors.length ? (
        <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs">
          {batchResult.errors.slice(0, 8).map((err) => (
            <li key={`${err.row}-${err.table || ''}-${err.key}-${err.reason}`}>
              Row {err.row}
              {err.table ? ` [${err.table}]` : ''}
              {err.key ? ` (${err.key})` : ''}: {err.reason}
            </li>
          ))}
          {batchResult.errors.length > 8 || batchResult.error_truncated ? (
            <li>…more errors omitted</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  ) : null

  return (
    <div className="space-y-4">
      {batchBanner}
      <SummaryDashboard
        title="Luminate Status Mapping"
        subtitle="Label / Artist / Track status"
        fetchData={fetchData}
        defaultSort={[
          { column: 'Entity Type', direction: 'asc' },
          { column: 'Status', direction: 'asc' },
          { column: 'Entity Key', direction: 'asc' },
        ]}
        searchPlaceholder="Search imprint, artist, song id, status…"
        editableStatus={editableStatus}
        onDeleteRow={onDeleteRow}
        addForm={addForm}
        extraActions={extraActions}
        layout="mapping"
        placeholderColumns={PLACEHOLDER_LUMINATE_MAPPING}
        tableKey={`luminate-${entityType}-${reloadNonce}`}
      />
    </div>
  )
}
